import { partnershipNotificationRoute } from '../../../../shared/src/utils/partnershipNotificationRoute';
import { agencyDocumentNotificationRoute } from '../../../../shared/src/utils/agencyDocumentNotificationRoute';
import { SidebarIdentity } from './SidebarIdentity';
import { visaNotificationRoute } from '../../../../shared/src/utils/visaNotificationRoute';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  Home,
  User,
  Bell,
  Users,
  Search
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { isApplicationDetailsRoute } from '@/utils/applicationDetailsRoute';
import { useSettings } from '@/contexts/SettingsContext';
import { agentApi } from '@/services/agentApi';
import { getLogoUrl } from '@/utils/logoUtils';
import { io, Socket } from 'socket.io-client';

// Custom hook for persistent sidebar state
const usePersistentSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      const isDesktop = window.innerWidth >= 992;
      const hasUserToggled = localStorage.getItem('agent-portal-sidebar-toggled') === 'true';
      const savedSidebarState = localStorage.getItem('agent-portal-sidebar-open');
      
      if (hasUserToggled && savedSidebarState !== null) {
        return savedSidebarState === 'true';
      }
      
      return isDesktop; // Open by default on desktop, closed on mobile
    }
    return true; // Default to open for SSR
  });

  const [userToggledSidebar, setUserToggledSidebar] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('agent-portal-sidebar-toggled');
      return saved === 'true';
    }
    return false;
  });

  const toggleSidebar = useCallback(() => {
    // Only allow toggling on mobile (desktop sidebar should always be open)
    // This check will be done at the call site, but we keep the function for mobile use
    const newSidebarState = !sidebarOpen;
    console.log('Toggling sidebar from', sidebarOpen, 'to', newSidebarState);
    
    setSidebarOpen(newSidebarState);
    setUserToggledSidebar(true);
    
    // Save preference to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('agent-portal-sidebar-toggled', 'true');
      localStorage.setItem('agent-portal-sidebar-open', newSidebarState.toString());
    }
  }, [sidebarOpen]);

  const restoreFromStorage = useCallback(() => {
    if (typeof window !== 'undefined') {
      const hasUserToggled = localStorage.getItem('agent-portal-sidebar-toggled') === 'true';
      const savedSidebarState = localStorage.getItem('agent-portal-sidebar-open');
      
      console.log('Restoring sidebar state from localStorage:', { hasUserToggled, savedSidebarState });
      
      if (hasUserToggled && savedSidebarState !== null) {
        const shouldBeOpen = savedSidebarState === 'true';
        console.log('Restoring sidebar state:', shouldBeOpen);
        setSidebarOpen(shouldBeOpen);
        setUserToggledSidebar(true);
      } else {
        // If no saved state, use default behavior based on screen size
        const isDesktopView = window.innerWidth >= 992;
        console.log('Using default sidebar state for screen size:', isDesktopView);
        setSidebarOpen(isDesktopView);
        setUserToggledSidebar(false);
      }
    }
  }, []);

  return {
    sidebarOpen,
    userToggledSidebar,
    toggleSidebar,
    restoreFromStorage
  };
};

const Sidebar: React.FC<{ isOpen: boolean; onClose: () => void; isDesktop?: boolean; applicationCount?: number; pendingEarnings?: number }> = ({ isOpen, onClose, isDesktop = false, applicationCount, pendingEarnings }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { getLogo, getTitle } = useSettings();
  const logoUrl = getLogo();
  const portalTitle = getTitle();
  const [profilePicture, setProfilePicture] = useState<string | null>(null);

  const resolveProfilePicture = useCallback((path?: string | null) => {
    const url = getLogoUrl(path || null);
    return url && url.trim() ? url : null;
  }, []);

  // Fetch profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      // Check if profile picture is already in user object
      const inlineProfile = (user as any)?.profilePicture;
      const inlineUrl = resolveProfilePicture(inlineProfile);
      if (inlineUrl) {
        setProfilePicture(inlineUrl);
        return;
      }

      // Otherwise, fetch from API
      try {
        const pictureUrl = await agentApi.getProfilePicture();
        if (pictureUrl) {
          setProfilePicture(resolveProfilePicture(pictureUrl));
        } else {
          setProfilePicture(null);
        }
      } catch (error: any) {
        // Ignore missing profile picture, log only unexpected errors
        if (error?.response?.status !== 404) {
          console.error('Failed to fetch profile picture:', error);
        }
      }
    };

    if (user) {
      fetchProfilePicture();
    }
  }, [user]);

  // Format pending earnings for badge display
  const formatEarningsBadge = (earnings: number | undefined): string | null => {
    if (earnings === undefined || earnings === null) return null;
    if (earnings === 0) return null; // Don't show badge if no pending earnings
    
    if (earnings >= 1000) {
      return `$${(earnings / 1000).toFixed(1)}k`;
    }
    return `$${Math.round(earnings)}`;
  };

  // Check if user is a sub-account
  const isSubAccount = user?.agentProfile?.parentAgentId !== undefined && user?.agentProfile?.parentAgentId !== null;
  const canAccessCommissions = !isSubAccount || user?.agentProfile?.canAccessCommissions === true;
  const canAccessPartnerships = !isSubAccount || user?.agentProfile?.canAccessPartnerships === true;

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: <Home size={20} />, badge: null },
    { path: '/courses', label: 'Course Search', icon: <Search size={20} />, badge: null },
    { path: '/applications', label: 'Applications', icon: <FileText size={20} />, badge: applicationCount !== undefined ? applicationCount.toString() : null },
    { path: '/eligibility', label: 'Eligibility checks', icon: <FileText size={20} />, badge: null },
    { path: '/student-profiles', label: 'Student Profiles', icon: <Users size={20} />, badge: null },
    ...(canAccessCommissions ? [{ path: '/commissions', label: 'Commissions', icon: <DollarSign size={20} />, badge: formatEarningsBadge(pendingEarnings) }] : []),
    { path: '/profile', label: 'Profile', icon: <User size={20} />, badge: null },
    { path: '/profile/documents', label: 'Agency Documents', icon: <FileText size={20} />, badge: null },
    ...(canAccessPartnerships ? [{ path: '/partnerships', label: 'Partnership', icon: <Users size={20} />, badge: null }] : []),
    ...(!isSubAccount ? [{ path: '/sub-accounts', label: 'Sub-Accounts', icon: <Users size={20} />, badge: null }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="d-lg-none position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
          style={{ zIndex: 1030 }}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{ 
          x: isDesktop ? 0 : (isOpen ? 0 : -280),
          opacity: isDesktop ? 1 : (isOpen ? 1 : 0),
          pointerEvents: isDesktop ? 'auto' : (isOpen ? 'auto' : 'none')
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="position-fixed top-0 start-0 h-100 bg-white shadow-lg d-flex flex-column"
        style={{ 
          width: '280px', 
          zIndex: 1040,
          transform: 'none', // Override any CSS transforms
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: '100vh'
        }}
      >
        {/* Header */}
        <div className="p-4 border-bottom sidebar-sticky-header">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <div style={{ width: '80%', height: 'auto', margin: 0, padding: 0 }}>
                  <img 
                    src={logoUrl || '/scholar_vest_logo_full.png'} 
                    alt={`${portalTitle} Agent Portal Logo`} 
                    className="img-fluid"
                    style={{ width: '100%', height: 'auto', objectFit: 'contain', margin: 0, padding: 0 }}
                  />
                </div>
              </div>
            <button 
              className="btn btn-link p-0 d-lg-none"
              onClick={onClose}
              aria-label="Close sidebar"
            >
              <X size={20} />
            </button>
            </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-bottom sidebar-sticky-profile">
          <div className="d-flex align-items-center">
            <img
              src={profilePicture || '/default-avatar.png'}
              alt={`${user?.firstName || 'Agent'} ${user?.lastName || ''}`.trim()}
              className="rounded-circle me-3"
              style={{ width: '40px', height: '40px', objectFit: 'cover', flexShrink: 0 }}
              onError={() => setProfilePicture(null)}
            />
            <SidebarIdentity accountId={user?.id} firstName={user?.firstName}
              lastName={user?.lastName} email={user?.email} />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-grow-1 p-3 sidebar-menu-scroll">
          <ul className="nav nav-pills flex-column">
            {menuItems.map((item) => (
              <li key={item.path} className="nav-item mb-2">
                <button
                  className={`nav-link w-100 text-start d-flex align-items-center justify-content-between ${
                    location.pathname === item.path ? 'active' : ''
                  }`}
                  onClick={() => {
                    navigate(item.path);
                    onClose();
                  }}
                  style={{ 
                    border: 'none',
                    backgroundColor: location.pathname === item.path ? 'var(--primary, #1e40af)' : 'transparent',
                    color: location.pathname === item.path ? 'white' : '#6b7280',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div className="d-flex align-items-center">
                    <span className="me-3">{item.icon}</span>
                    {item.label}
                  </div>
                  {item.badge && (
                    <span 
                      className="badge bg-light text-dark"
                      style={{ 
                        fontSize: '0.7rem',
                        backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : '#f8f9fa',
                        color: location.pathname === item.path ? 'white' : '#6b7280'
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Cookie preferences & Logout */}
        <div className="p-3 border-top sidebar-sticky-logout">
          <a
            href="#cookie-preferences"
            className="btn btn-link w-100 text-start text-muted small text-decoration-none mb-2"
          >
            Cookie preferences
          </a>
          <button
            className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center"
            onClick={handleLogout}
          >
            <LogOut size={18} className="me-2" />
            Logout
          </button>
        </div>
      </motion.div>
    </>
  );
};

const Header: React.FC<{ onMenuClick: () => void; isDesktop: boolean; sidebarOpen: boolean; elementRef: React.RefObject<HTMLElement> }> = ({ onMenuClick, isDesktop, sidebarOpen, elementRef }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Array<{
    id: number;
    type: string;
    title: string;
    message: string;
    data?: Record<string, any>;
    isRead: boolean;
    createdAt: string;
  }>>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);

  const resolveProfilePicture = useCallback((path?: string | null) => {
    const url = getLogoUrl(path || null);
    return url && url.trim() ? url : null;
  }, []);
  const socketRef = useRef<Socket | null>(null);

  // Fetch profile picture (always use main agent's picture for sub-accounts)
  useEffect(() => {
    const fetchProfilePicture = async () => {
      // Check if user is a sub-account
      const isSubAccount = user?.agentProfile?.parentAgentId !== undefined && user?.agentProfile?.parentAgentId !== null;
      
      // For sub-accounts, always fetch from API to get main agent's picture
      // For main agents, check user object first, then fetch from API if not available
      if (!isSubAccount && (user as any)?.profilePicture) {
        setProfilePicture(resolveProfilePicture((user as any).profilePicture));
        return;
      }

      // Fetch from API (will return main agent's picture for sub-accounts)
      try {
        const pictureUrl = await agentApi.getProfilePicture();
        if (pictureUrl) {
          setProfilePicture(resolveProfilePicture(pictureUrl));
        } else {
          setProfilePicture(null);
        }
      } catch (error: any) {
        if (error?.response?.status !== 404) {
          console.error('Failed to fetch profile picture:', error);
        }
      }
    };

    if (user) {
      fetchProfilePicture();
    }
  }, [user]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await agentApi.getUnreadNotificationCount();
      setUnreadCount(count);
    } catch (error: any) {
      // Silently handle 401 errors (expected when user is not authenticated)
      if (error?.response?.status !== 401) {
        console.error('Error fetching unread notification count:', error);
      }
    }
  }, []);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const result = await agentApi.getNotifications({ page: 1, limit: 10 });
      setNotifications(result.notifications);
    } catch (error: any) {
      // Silently handle 401 errors (expected when user is not authenticated)
      if (error?.response?.status !== 401) {
        console.error('Error fetching notifications:', error);
      }
    }
  }, []);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (notificationId: number) => {
    try {
      await agentApi.markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [fetchUnreadCount]);

  // Mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await agentApi.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  // Handle notification click
  const handleNotificationClick = useCallback((notification: typeof notifications[0]) => {
    if (!notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    // Only use the canonical workspace for this authenticated agency.
    if (notification.data?.entityType === 'agency_document') {
      const destination = agencyDocumentNotificationRoute(notification.data, user?.agentProfile?.parentAgentId || user?.agentProfile?.id);
      if (destination) { navigate(destination); setShowNotifications(false); }
      return;
    }
    if (notification.type === 'PARTNERSHIP_UPDATE' || notification.data?.entityType === 'partnership') {
      const destination = partnershipNotificationRoute(notification.data);
      if (destination) { navigate(destination); setShowNotifications(false); }
      return;
    }
    if (notification.type === 'ELIGIBILITY_REVIEW' && Number.isSafeInteger(Number(notification.data?.checkId)) && Number(notification.data?.checkId) > 0) {
      navigate(`/eligibility/checks/${Number(notification.data?.checkId)}`); setShowNotifications(false); return;
    }
    // Navigate based on notification type
    if (notification.type === 'APPLICATION_UPDATE' && notification.data?.applicationId) {
      navigate(visaNotificationRoute(notification.data) || `/applications/${notification.data.applicationId}`);
      setShowNotifications(false);
    } else if (notification.type === 'COMMISSION_PAYOUT' && notification.data?.commissionId) {
      navigate(`/commissions/${notification.data.commissionId}`);
      setShowNotifications(false);
    }
  }, [handleMarkAsRead, navigate, user?.agentProfile?.id, user?.agentProfile?.parentAgentId]);

  // Format time ago
  const formatTimeAgo = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
      return `${diffInWeeks}w ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths}mo ago`;
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    
    fetchUnreadCount();
    fetchNotifications();
  }, [user, fetchUnreadCount, fetchNotifications]);

  // Setup socket.io connection for real-time notifications
  useEffect(() => {
    if (!user) return;

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
    const socketUrl = API_BASE_URL.replace('/api/v1', '');
    const token = localStorage.getItem('token');

    if (!token) return;

    // Create socket connection
    const socket = io(socketUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling']
    });

    socketRef.current = socket;

    // Connection event
    socket.on('connect', () => {
      console.log('Socket connected for notifications');
      // Subscribe to notifications
      socket.emit('subscribe_notifications');
    });

    // Listen for new notifications
    socket.on('new_notification', (data: { notification?: any }) => {
      if (!data?.notification) {
        void fetchUnreadCount();
        if (showNotifications) void fetchNotifications();
        return;
      }
      const newNotification = data.notification;
      // Add to notifications list if not already present
      setNotifications(prev => {
        const exists = prev.some(n => n.id === newNotification.id);
        if (exists) return prev;
        return [newNotification, ...prev].slice(0, 10); // Keep only latest 10
      });
      // Update unread count
      if (!newNotification.isRead) {
        setUnreadCount(prev => prev + 1);
      }
      // Refresh notifications if dropdown is open
      if (showNotifications) {
        fetchNotifications();
      }
    });

    // Listen for unread count updates
    socket.on('notification_count_update', (data: { unreadCount: number }) => {
      setUnreadCount(data.unreadCount);
    });

    // Error handling
    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, showNotifications, fetchNotifications, fetchUnreadCount]);

  // Poll for new notifications every 30 seconds (fallback if socket fails)
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const interval = setInterval(() => {
      fetchUnreadCount();
      if (showNotifications) {
        fetchNotifications();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [user, fetchUnreadCount, fetchNotifications, showNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  const handleMenuClick = () => {
    // Only allow menu click on mobile
    if (!isDesktop) {
      console.log('Menu button clicked on mobile:', { isDesktop, sidebarOpen });
      onMenuClick();
    }
  };

  return (
    <header ref={elementRef} className="bg-white shadow-sm border-bottom position-sticky top-0" style={{ zIndex: 1050, position: 'sticky', top: 0 }}>
      <div className="d-flex align-items-center justify-content-between p-3">
        <div className="d-flex align-items-center">
          <button 
            className="btn btn-link p-0 me-3 d-lg-none"
            onClick={handleMenuClick}
            title="Open Menu"
          >
            <Menu size={24} />
          </button>
          <h4 className="fw-bold mb-0 text-dark">Agent Portal</h4>
        </div>
        
        <div className="d-flex align-items-center">
          <div className="position-relative me-2" ref={notificationRef}>
            <button 
              className="btn btn-link p-2 position-relative"
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (!showNotifications) {
                  fetchNotifications();
                }
              }}
            >
              <Bell size={20} className="text-muted" />
              {unreadCount > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem', minWidth: '18px' }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            
            {showNotifications && (
              <div 
                className="position-absolute mt-2 bg-white border rounded shadow-lg notification-dropdown"
                style={{ 
                  overflowY: 'auto',
                  zIndex: 1060,
                  top: '100%'
                }}
              >
                <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
                  <h6 className="mb-0 fw-bold">Notifications</h6>
                  <button 
                    className="btn btn-sm btn-link text-primary p-0"
                    onClick={handleMarkAllAsRead}
                    disabled={unreadCount === 0}
                  >
                    Mark all as read
                  </button>
                </div>
                
                <div className="list-group list-group-flush">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted">
                      <p className="mb-0">No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`list-group-item list-group-item-action ${
                          !notification.isRead ? 'notification-unread' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="d-flex align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <h6 className="mb-0 fw-semibold me-2">{notification.title}</h6>
                              {!notification.isRead && (
                                <span className="badge rounded-pill notification-new-badge" style={{ fontSize: '0.5rem' }}>New</span>
                              )}
                            </div>
                            <p className="mb-1 text-muted small">{notification.message}</p>
                            <small className="text-muted">{formatTimeAgo(notification.createdAt)}</small>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {notifications.length > 0 && (
                  <div className="p-2 border-top text-center">
                    <button 
                      className="btn btn-sm btn-link text-primary"
                      onClick={() => navigate('/notifications')}
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="dropdown">
            <button 
              className="btn btn-link p-0 d-flex align-items-center"
              data-bs-toggle="dropdown"
            >
              <img
                src={profilePicture || '/default-avatar.png'}
                alt={`${user?.firstName || 'Agent'} ${user?.lastName || ''}`.trim()}
                className="rounded-circle me-2"
                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                onError={() => setProfilePicture(null)}
              />
              <span className="text-dark fw-semibold">
                {user?.agentProfile?.agencyName || 'Agent'}
              </span>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><a className="dropdown-item" href="/profile">Profile</a></li>
              <li><a className="dropdown-item" href="/settings">Settings</a></li>
              <li><hr className="dropdown-divider" /></li>
              <li><a className="dropdown-item text-danger" href="/logout">Logout</a></li>
            </ul>
          </div>
        </div>
      </div>
    </header>
  );
};

export const Layout: React.FC = () => {
  const layoutRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const applicationDetails = isApplicationDetailsRoute(location.pathname);

  useLayoutEffect(() => {
    if (!applicationDetails || !headerRef.current || !layoutRef.current) return;
    const header = headerRef.current;
    const shell = layoutRef.current;
    const measure = () => shell.style.setProperty('--application-header-height', `${header.getBoundingClientRect().height}px`);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(header);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      shell.style.removeProperty('--application-header-height');
    };
  }, [applicationDetails]);
  
  // Use the persistent sidebar hook
  const { sidebarOpen, toggleSidebar } = usePersistentSidebar();
  
  // Initialize with proper values to prevent layout flash
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 992;
    }
    return true; // Default to desktop for SSR
  });
  
  const [isRouteChanging, setIsRouteChanging] = useState(false);
  const [applicationCount, setApplicationCount] = useState<number | undefined>(undefined);
  const [pendingEarnings, setPendingEarnings] = useState<number | undefined>(undefined);

  // Fetch application count and pending earnings on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const stats = await agentApi.getStats();
        setApplicationCount(stats.totalApplications);
        setPendingEarnings(stats.pendingEarnings);
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Keep count as undefined on error, badge won't show
      }
    };

    fetchStats();
  }, []);

  // Refresh application count and pending earnings when navigating to/from applications or commissions routes
  useEffect(() => {
    if (location.pathname.startsWith('/applications') || location.pathname.startsWith('/commissions')) {
      const fetchStats = async () => {
        try {
          const stats = await agentApi.getStats();
          setApplicationCount(stats.totalApplications);
          setPendingEarnings(stats.pendingEarnings);
        } catch (error) {
          console.error('Error fetching stats:', error);
        }
      };

      fetchStats();
    }
  }, [location.pathname]);

  // Ensure sidebar is always open on desktop
  useEffect(() => {
    if (isDesktop && !sidebarOpen) {
      // Force sidebar open on desktop
      if (typeof window !== 'undefined') {
        localStorage.setItem('agent-portal-sidebar-open', 'true');
      }
      toggleSidebar();
    }
  }, [isDesktop, sidebarOpen, toggleSidebar]);

  // Handle responsive behavior - only on window resize, not on route changes
  useEffect(() => {
    const handleResize = () => {
      const isDesktopView = window.innerWidth >= 992;
      setIsDesktop(isDesktopView);
      
      // On desktop, always show sidebar (force it open)
      if (isDesktopView && !sidebarOpen) {
        // Force sidebar open on desktop
        if (typeof window !== 'undefined') {
          localStorage.setItem('agent-portal-sidebar-open', 'true');
        }
        // Use toggleSidebar to open it if it's closed
        if (!sidebarOpen) {
          toggleSidebar();
        }
      }
    };

    // Listen for window resize only
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [isDesktop, sidebarOpen, toggleSidebar]); // Include sidebarOpen and toggleSidebar in dependencies

  // Prevent layout distortion during route changes
  useEffect(() => {
    if (layoutRef.current) {
      // Force a reflow to ensure layout is stable
      layoutRef.current.offsetHeight;
    }
  }, [isDesktop, sidebarOpen]);

  // Debug sidebar state changes
  useEffect(() => {
    console.log('Sidebar state changed:', { isDesktop, sidebarOpen, isRouteChanging });
  }, [isDesktop, sidebarOpen, isRouteChanging]);


  // Ensure layout stability during route changes - without affecting sidebar state
  useEffect(() => {
    setIsRouteChanging(true);
    
    // Small delay to ensure DOM is updated after route change
    const timer = setTimeout(() => {
      if (layoutRef.current && contentRef.current) {
        // Force layout recalculation
        layoutRef.current.style.transform = 'translateZ(0)';
        contentRef.current.style.transform = 'translateZ(0)';
        
        // Force reflow to ensure proper alignment
        layoutRef.current.offsetHeight;
        contentRef.current.offsetHeight;
        
        // Ensure proper width calculation
        if (isDesktop) {
          const expectedWidth = sidebarOpen ? 'calc(100% - 280px)' : '100%';
          layoutRef.current.style.width = expectedWidth;
        }
        
        // Reset route changing state
        setIsRouteChanging(false);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname]); // Only depend on pathname, not on sidebar state

  // Close sidebar function - only works on mobile
  const closeSidebar = useCallback(() => {
    // Only allow closing on mobile
    if (!isDesktop && sidebarOpen) {
      toggleSidebar();
    }
  }, [isDesktop, sidebarOpen, toggleSidebar]);

  // Mobile-only toggle function
  const handleMobileToggle = useCallback(() => {
    if (!isDesktop) {
      toggleSidebar();
    }
  }, [isDesktop, toggleSidebar]);

  return (
    <div className="d-flex layout-stable">
      <Sidebar isOpen={isDesktop ? true : sidebarOpen} onClose={closeSidebar} isDesktop={isDesktop} applicationCount={applicationCount} pendingEarnings={pendingEarnings} />
      
      <div 
        ref={layoutRef}
        className={`flex-grow-1 d-flex flex-column layout-transition content-alignment-stable ${applicationDetails ? 'application-details-shell' : ''} ${
          isDesktop 
            ? `main-content-desktop ${isRouteChanging ? 'route-changing' : ''}` 
            : 'main-content-mobile'
        }`}
        style={{ 
          minHeight: '100vh',
          marginLeft: isDesktop ? '280px' : (sidebarOpen ? '0' : '0'),
          width: isDesktop ? 'calc(100% - 280px)' : '100%',
          transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out'
        }}
      >
        <Header elementRef={headerRef} onMenuClick={handleMobileToggle} isDesktop={isDesktop} sidebarOpen={sidebarOpen} />
        
        <main ref={contentRef} className="flex-grow-1 bg-light content-container route-stable">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="content-container route-stable"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
};
